import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => new Set());

  useEffect(() => {
    if (!user) { setIds(new Set()); return; }
    api.get("/wishlist/ids").then((r) => setIds(new Set(r.data.ids || []))).catch(() => {});
  }, [user]);

  const toggle = useCallback(async (productId) => {
    if (!user) {
      toast("Sign in to save favorites", { description: "Create a free account to build your wishlist." });
      return false;
    }
    const had = ids.has(productId);
    setIds((prev) => { const n = new Set(prev); if (had) n.delete(productId); else n.add(productId); return n; });
    try {
      const { data } = await api.post("/wishlist/toggle", { product_id: productId });
      toast.success(data.wishlisted ? "Added to your wishlist" : "Removed from wishlist");
      return data.wishlisted;
    } catch (e) {
      setIds((prev) => { const n = new Set(prev); if (had) n.add(productId); else n.delete(productId); return n; });
      toast.error(errMsg(e));
      return had;
    }
  }, [user, ids]);

  return (
    <WishlistContext.Provider value={{ ids, has: (id) => ids.has(id), toggle, count: ids.size }}>
      {children}
    </WishlistContext.Provider>
  );
}

export const useWishlist = () =>
  useContext(WishlistContext) || { ids: new Set(), has: () => false, toggle: () => {}, count: 0 };
